import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { navLinks, services, ecosystem, BOOK_MEETING_URL } from "../mock";
import Logo from "./Logo";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  const onLight = true;
  const baseText = "text-[#0F2A4A]";

  return (
    <header
      className={`fixed top-0 z-50 w-full bg-white/95 backdrop-blur transition-all duration-300 ${
        scrolled ? "shadow-md" : "shadow-sm"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-8">
        <Link to="/" className="flex items-center">
          <Logo dark={!onLight} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-9 lg:flex">
          {navLinks.map((link) =>
            link.dropdown ? (
              <div key={link.label} className="group relative">
                <button
                  className={`flex items-center gap-1 text-[15px] font-medium tracking-tight transition-colors duration-200 hover:text-[#E4545F] ${baseText}`}
                >
                  {link.label}
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                </button>
                <div className="invisible absolute left-1/2 top-full z-50 w-80 -translate-x-1/2 translate-y-3 rounded-2xl border border-slate-100 bg-white p-2 opacity-0 shadow-2xl transition-all duration-200 group-hover:visible group-hover:translate-y-2 group-hover:opacity-100">
                  {link.dropdown === "services" &&
                    services.map((s) => (
                      <Link
                        key={s.id}
                        to={`/services/${s.id}`}
                        className="block rounded-xl px-4 py-2.5 text-sm text-[#0F2A4A] transition-colors duration-150 hover:bg-slate-50 hover:text-[#E4545F]"
                      >
                        {s.title}
                      </Link>
                    ))}
                  {link.dropdown === "ecosystem" &&
                    ecosystem.branches.map((b) => (
                      <a
                        key={b.id}
                        href={b.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between rounded-xl px-4 py-3 transition-colors duration-150 hover:bg-slate-50"
                      >
                        <span>
                          <span className="block text-sm font-semibold text-[#0F2A4A]">
                            {b.name}
                          </span>
                          <span className="block text-xs text-slate-500">
                            {b.tagline}
                          </span>
                        </span>
                        <ArrowUpRight className="h-4 w-4 text-slate-400" />
                      </a>
                    ))}
                </div>
              </div>
            ) : (
              <NavLink
                key={link.label}
                to={link.path}
                end={link.path === "/"}
                className={({ isActive }) =>
                  `text-[15px] font-medium tracking-tight transition-colors duration-200 hover:text-[#E4545F] ${
                    isActive && onLight ? "text-[#E4545F]" : baseText
                  }`
                }
              >
                {link.label}
              </NavLink>
            )
          )}
          <a
            href={BOOK_MEETING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 ${
              onLight
                ? "bg-[#0F2A4A] text-white hover:bg-[#0b2038]"
                : "bg-white text-[#0F2A4A] hover:bg-white/90"
            }`}
          >
            Let’s Connect
          </a>
        </nav>

        <button
          className={`flex h-10 w-10 items-center justify-center rounded-md lg:hidden ${baseText}`}
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="menu"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      <div
        className={`overflow-hidden bg-white transition-all duration-300 lg:hidden ${
          mobileOpen ? "max-h-[640px] border-t border-slate-100" : "max-h-0"
        }`}
      >
        <nav className="flex flex-col px-4 py-2">
          {navLinks.map((link) =>
            link.dropdown ? (
              <div key={link.label} className="border-b border-slate-100 py-1">
                <button
                  onClick={() => setOpenGroup((g) => (g === link.label ? null : link.label))}
                  className="flex w-full items-center justify-between py-3 text-left font-medium text-[#0F2A4A]"
                >
                  {link.label}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${openGroup === link.label ? "rotate-180" : ""}`}
                  />
                </button>
                {openGroup === link.label && (
                  <div className="flex flex-col pb-2 pl-3">
                    {link.dropdown === "services" &&
                      services.map((s) => (
                        <Link
                          key={s.id}
                          to={`/services/${s.id}`}
                          className="py-2 text-sm text-slate-600 hover:text-[#E4545F]"
                        >
                          {s.title}
                        </Link>
                      ))}
                    {link.dropdown === "ecosystem" &&
                      ecosystem.branches.map((b) => (
                        <a
                          key={b.id}
                          href={b.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 py-2 text-sm text-slate-600 hover:text-[#E4545F]"
                        >
                          {b.name} <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                      ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={link.label}
                to={link.path}
                end={link.path === "/"}
                className="border-b border-slate-100 py-3 font-medium text-[#0F2A4A] hover:text-[#E4545F]"
              >
                {link.label}
              </NavLink>
            )
          )}
          <a
            href={BOOK_MEETING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 rounded-full bg-[#0F2A4A] px-5 py-3 text-center text-sm font-semibold text-white"
          >
            Let’s Connect
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Header;
